import { baseApiAxios } from './js/portal/api';
import TileDonutChart from './jsx/components/tiles/TileDonutChart';
import TileText from './jsx/components/tiles/TileText';
import ChartHorizontalStacked from './jsx/components/tiles/ChartHorizontalStacked';
import ChartLegend from './jsx/components/tiles/ChartLegend';
import {
  defaultColors,
  loadDonutChart,
  loadOrderStatistics,
  loadStackedAbsoluteValuesChart,
  loadTextTile,
  parseInitialTimeframeOptions,
  createTimeframeController,
  StatisticsPageHeader,
  withStatsLoading,
} from './js/portal/statistics';

export default async function StatisticsAccountPage() {
  let accountStageData = {},
    accountAreaData = {},
    orgIdData = {};

  const controller = createTimeframeController({
    loadInitialData: async (radioSelection, dropdownSelection) => {
      await loadOrgIdData(radioSelection, dropdownSelection);
      return parseInitialTimeframeOptions(orgIdData?.stats, radioSelection);
    },
    loadEmptyTiles,
    loadDashboardData,
    getStatsData: () => orgIdData,
  });

  const page = (
    <div id="dashboard_overview_account">
      <StatisticsPageHeader
        title="Account Statistics"
        dropdownChangeListener={controller.dropdownChangeListener}
        radioChangeListener={controller.radioChangeListener}
      />
      <div class="row">
        <TileText id="number-active-accounts" title="Active Accounts"></TileText>
        <TileText id="number-closed-accounts" title="Closed Accounts"></TileText>
        <TileText id="number-external-accounts" title="External Accounts"></TileText>
        <TileDonutChart
          id="accounts-by-area"
          title="Accounts by Area"
          labels={[]}
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileText id="accounts-by-area-and-type" title="Number of Accounts" headline="by Type and Area">
          <ChartHorizontalStacked
            title=""
            hideLegend={true}
            values={[]}
            colors={defaultColors}></ChartHorizontalStacked>
          <ChartLegend labels={[]} colors={defaultColors}></ChartLegend>
        </TileText>
        <TileDonutChart
          id="accounts-by-stage"
          title="Accounts by Stage"
          labels={[]}
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileDonutChart
          id="accounts-by-org"
          title="Accounts by Organization"
          labels={[]}
          colors={defaultColors}
          partial={26 / 30}></TileDonutChart>
        <TileText id="create-account" title="Create Account"></TileText>
        <TileText id="account-close" title="Close Account"></TileText>
      </div>
    </div>
  );

  async function onPageReady() {
    controller.onPageReady();
  }

  // LOAD AND DISPLAY DATA

  function loadEmptyTiles() {
    loadTextTile('number-active-accounts', '-', null, true);
    loadTextTile(
      'number-closed-accounts',
      '-',
      null,
      true,
      undefined,
      undefined,
      'Closed within the last 90 days, awaiting permanent deletion',
    );
    loadTextTile('number-external-accounts', '-', null, true);
    loadTextTile('create-account', '-', null, true);
    loadTextTile('account-close', '-', null, true);
    loadDonutChart('accounts-by-area', 'Accounts by Area', {});
    loadDonutChart('accounts-by-type', 'Accounts by Type', {});
    loadStackedAbsoluteValuesChart(
      'accounts-by-area-and-type',
      'Number of Accounts',
      'by Type and Area',
      {},
      [],
      10,
      true,
      true,
    );
    loadDonutChart('accounts-by-stage', 'Accounts by Stage', {});
    loadDonutChart('accounts-by-org', 'Accounts by Organization', {});
  }

  function loadDashboardData(timeframe, sk) {
    loadOrderStatistics('create-account', 'orders', 'create-account', 'monthly', sk);
    loadOrderStatistics('account-close', 'orders', 'account-close', 'monthly', sk);
    loadAccountAreaData(timeframe, sk);
    loadAccountStageData(timeframe, sk);
    loadOrgIdData(timeframe, sk);
  }

  async function _fetchAccountAreaData(timeframe, sk) {
    accountAreaData = await baseApiAxios.get(
      `/stats/categories/accounts/statistics/account_area?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
      null,
      {},
    );

    if (accountAreaData?.stats?.length) {
      let chartData = {};
      let areaData = {};
      let activeAccounts = 0;
      let closedAccounts = 0;
      let typeData = {};

      Object.entries(accountAreaData.stats[0].data).forEach(([area, values]) => {
        if (area === 'count') return;
        let numberByType = {};
        if (values.active) {
          areaData[area] = values.active.count;
          activeAccounts += values.active.count;
          let activeAccountData = values.active;
          delete activeAccountData['count'];
          Object.entries(activeAccountData).forEach(([type, count]) => {
            typeData[type] = (typeData[type] || 0) + count;
            if (!numberByType[type]) numberByType[type] = {};
            numberByType[type][area] = count;
          });
        }
        if (values.inactive) {
          areaData[area] = (areaData[area] || 0) + values.inactive.count;
          closedAccounts += values.inactive.count;
          let inactiveAccountData = values.inactive;
          delete inactiveAccountData['count'];
          Object.entries(inactiveAccountData).forEach(([type, count]) => {
            typeData[type] = (typeData[type] || 0) + count;
            if (!chartData[type]) chartData[type] = { count: 0 };
            chartData[type][area] = { count: (numberByType[type]?.[area] || 0) + count };
            chartData[type]['count'] += chartData[type][area]['count'];
          });
        }
      });

      loadTextTile('number-active-accounts', activeAccounts, null, true);
      loadTextTile(
        'number-closed-accounts',
        closedAccounts || 0,
        null,
        true,
        undefined,
        undefined,
        'Closed within the last 90 days, awaiting permanent deletion',
      );
      loadTextTile('number-external-accounts', typeData['external'] || 0, null, true);
      loadDonutChart('accounts-by-area', 'Accounts by Area', areaData, 'Active + Closed');
      loadDonutChart('accounts-by-type', 'Accounts by Type', typeData, 'Active + Closed');
      loadStackedAbsoluteValuesChart(
        'accounts-by-area-and-type',
        'Number of Accounts',
        'by Type and Area',
        chartData,
        Object.keys(areaData),
        Math.max(...Object.values(typeData)),
        true,
        true,
      );
    }
  }

  async function loadAccountAreaData(timeframe, sk) {
    await withStatsLoading(() => _fetchAccountAreaData(timeframe, sk));
  }

  async function loadAccountStageData(timeframe, sk) {
    await withStatsLoading(async () => {
      accountStageData = await baseApiAxios.get(
        `/stats/categories/accounts/statistics/account_stage?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      if (accountStageData?.stats?.length) {
        let chartData = {};
        Object.entries(accountStageData.stats[0].data)
          .filter(([_stage, values]) => values.count)
          .forEach(([stage, values]) => {
            chartData[stage] = values.count;
          });
        loadDonutChart('accounts-by-stage', 'Accounts by Stage', chartData, 'Active + Closed');
      }
    });
  }

  async function loadOrgIdData(timeframe, sk) {
    await withStatsLoading(async () => {
      orgIdData = await baseApiAxios.get(
        `/stats/categories/accounts/statistics/org_id?time_frame=${timeframe}${sk ? '&sort_key=' + sk : ''}`,
        null,
        {},
      );

      if (orgIdData?.stats?.length) {
        let chartData = orgIdData.stats[0].data;
        delete chartData['count'];
        loadDonutChart('accounts-by-org', 'Accounts by Organization', chartData, 'Active + Closed');
      }
    });
  }

  return [page, onPageReady];
}
